import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'

import { Transforms } from 'slate'
import { RenderLeafProps, useSlateStatic } from 'slate-react'
import { useMediaPlayerContext } from '@verbit-ai/verbit-connect-web/player-provider'

import { SlateEditor } from 'src/slate/SlateEditor'
import { SlateProvider } from 'src/slate/SlateContext'
import { toSlateDescendant } from 'src/slate/types'
import { TranscriptParagraph } from 'src/models/Transcript'
import { withTranscript } from 'src/slate/plugins/withTranscript'
import { useTranscriptClient, useTranscriptUserStateManager } from 'src/contexts/Transcript/TranscriptContext'
import { TranscriptEditor } from 'src/slate/plugins/TranscriptEditor'
import { reconcileParagraphLeaves } from 'src/slate/plugins/reconcileParagraphLeaves'
import { handleLineBreakTextInput } from 'src/slate/plugins/handleLineBreakTextInput'
import { HOTKEYS } from 'src/utils/hotkeys'
import { analyticsService } from 'src/services/AnalyticsService'
import { useEditor } from 'src/contexts/EditorContext'
import { useTranscriptMetadata } from 'src/contexts/Transcript/MetadataContext'
import { isJobLive, isRecordingStopped } from 'src/models/TranscriptMetadata'
import { useFeatureFlag } from 'src/contexts/FeatureFlagContext'
import { useToastWithAnnouncement } from 'src/hooks/useToastWithAnnouncement'
import { useVariableRef } from 'src/hooks/useVariableRef'
import { isRtBrokerTranscriptClient } from 'src/network/RtBrokerTranscriptClientV2'

import { ContentEntry, contentEntryPresentation, ParagraphTextProps, ParagraphTextWrapper, useGreyOutUnfinalizedAsr } from './shared'

type ParagraphSlateEditorProps = {
    textElementRef: React.Ref<HTMLDivElement>
    paragraph: TranscriptParagraph
}

const ParagraphSlateEditor = (props: ParagraphSlateEditorProps) => {
    const { textElementRef, paragraph } = props

    const editor = useSlateStatic()
    const transcriptClient = useTranscriptClient()
    const userStateManager = useTranscriptUserStateManager()
    const viewMode = useTranscriptMetadata()?.viewMode
    const { isTranscriptEditAuthorised } = useEditor()
    const greyOutUnfinalizedAsr = useGreyOutUnfinalizedAsr()

    const renderLeaf = useCallback(
        (renderLeafProps: RenderLeafProps) => {
            const { leaf, attributes, children } = renderLeafProps
            const { isEditable } = leaf
            const { isGreyedOut, isSelectable } = contentEntryPresentation({
                viewMode,
                isTranscriptEditAuthorised,
                isEditable,
                greyOutUnfinalizedAsr,
            })

            return (
                <ContentEntry
                    // eslint rule for attributes spreading is disabled, because we need that,
                    // as Slate relies on data attributes to maintain it's functionality
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...attributes}
                    $isGreyedOut={isGreyedOut}
                    $isSelectable={isSelectable}>
                    {children}
                </ContentEntry>
            )
        },
        [greyOutUnfinalizedAsr, isTranscriptEditAuthorised, viewMode],
    )

    useEffect(() => {
        if (!isRtBrokerTranscriptClient(transcriptClient) || !Array.isArray(editor.children)) {
            return
        }

        return transcriptClient.subscribeRemoteParagraphText(paragraph.id, (notice) => {
            if (notice.kind === 'delta') {
                TranscriptEditor.applyDelta(editor, notice.delta)

                return
            }

            reconcileParagraphLeaves(editor, transcriptClient.getParagraphById(paragraph.id).content)
        })
    }, [editor, paragraph.id, transcriptClient])

    useLayoutEffect(() => {
        if (!Array.isArray(editor.children)) {
            return
        }

        reconcileParagraphLeaves(editor, paragraph.content)
    }, [editor, paragraph.content])

    // set the initial selection based on the local user state
    useEffect(() => {
        const localState = userStateManager.getLocalState()

        if (localState.selection && localState.selection.paragraphId === paragraph.id) {
            const { offset } = localState.selection
            const startSelection = TranscriptEditor.getPointFromParagraphOffset(editor, [0], offset)

            if (startSelection) {
                Transforms.select(editor, startSelection)
            }
        }
        // make sure this effect runs only on mount
    }, [editor, paragraph.id, userStateManager])

    const onKeyDownRootHandler = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (HOTKEYS.escape.check(event)) {
                event.preventDefault()
                userStateManager.updateSelection(null)
            }
        },
        [userStateManager],
    )

    const keyDownHandlers = useMemo(() => [onKeyDownRootHandler], [onKeyDownRootHandler])

    const onBlur = () => {
        userStateManager.updateSelection(null)
    }

    // The result must reach Slate: on Android beforeinput is usually not cancelable.
    const onDOMBeforeInput = useCallback((event: InputEvent) => handleLineBreakTextInput(editor, event), [editor])

    return (
        <SlateEditor
            ref={textElementRef}
            onKeyDownHandlers={keyDownHandlers}
            onBlur={onBlur}
            onDOMBeforeInput={onDOMBeforeInput}
            renderLeaf={renderLeaf}
        />
    )
}

export const ParagraphTextEditable = (props: ParagraphTextProps) => {
    const { textElementRef, paragraph, color, backgroundColor } = props
    const transcriptClient = useTranscriptClient()
    const meta = useTranscriptMetadata()
    const showToast = useToastWithAnnouncement()
    const { mediaPlayer } = useMediaPlayerContext()
    const isTextHighlightingEnabled = useFeatureFlag('text_highlighting')
    const isParagraphSplitMergeEnabled = useFeatureFlag('paragraph_split_merge')

    // The plugin is built once per mount; the refs keep the flags and player current.
    const splitMergeEnabledRef = useVariableRef(isParagraphSplitMergeEnabled)
    const pauseOnTextEditRef = useVariableRef(() => {
        if (isTextHighlightingEnabled) {
            mediaPlayer.pause()
        }
    })

    const [slatePlugins] = useState(() => [
        withTranscript({
            paragraph,
            transcriptClient,
            onContentChange: () =>
                analyticsService.track('Verbitext 3.0 - Transcript - Transcript edited', {
                    'When edited': isJobLive(meta!)
                        ? isRecordingStopped(meta!)
                            ? 'After live session ended'
                            : 'During live session'
                        : 'Post-production',
                }),
            onTextEdit: () => pauseOnTextEditRef.current(),
            isParagraphSplitMergeEnabled: () => splitMergeEnabledRef.current,
            onPasteTooLarge: () =>
                showToast({
                    id: 'paste-too-long',
                    type: 'error',
                    title: 'Paste is too long',
                    description: 'Try pasting a shorter section.',
                }),
        }),
    ])
    const initialValue = useMemo(() => [toSlateDescendant(paragraph.content)], [paragraph.content])

    return (
        <ParagraphTextWrapper data-paragraph-id={paragraph.id} $color={color} $backgroundColor={backgroundColor}>
            <SlateProvider plugins={slatePlugins} initialValue={initialValue}>
                <ParagraphSlateEditor textElementRef={textElementRef} paragraph={paragraph} />
            </SlateProvider>
        </ParagraphTextWrapper>
    )
}
